import React, { useState, useEffect } from 'react';
import { apiClient } from '../../api/client';
import { AcquisitionProfile } from '../../types';
import { FieldGroup } from '../ui/FieldGroup';
import { AssetClassPicker, PickerAssetClass } from '../shared/AssetClassPicker';
import { COUNTRIES, labelFor } from '../../lib/countries';

interface ProfileFormProps {
  companyId: string;
  /** IDs der Assetklassen, für die der Investor bereits ein Profil hat (für Picker-Disable) */
  existingAssetClassIds?: string[];
  onSuccess: () => void;
  onCancel: () => void;
  initial?: AcquisitionProfile;
}

const inp: React.CSSProperties = {
  width: '100%', padding: '8px 12px', border: '1px solid var(--border2)',
  borderRadius: 'var(--radius)', fontSize: 13, background: 'var(--surface)', color: 'var(--text)',
};
const lbl: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 500,
  textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text3)', marginBottom: 4,
};
const sectionHead: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.8px',
  color: 'var(--accent)', marginBottom: 10, marginTop: 4,
  paddingBottom: 6, borderBottom: '1px solid var(--accent-light)',
};
const numInp: React.CSSProperties = { ...inp, fontFamily: 'DM Mono, monospace' };

/** Multi-Country-Picker: + Button öffnet Dropdown mit allen Ländern, ausgewählte als entfernbare Chips */
function CountryMultiSelect({ selected, onChange }: { selected: string[]; onChange: (v: string[]) => void }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const available = COUNTRIES.filter(c => !selected.includes(c.code) &&
    (search.trim() === '' || c.label.toLowerCase().includes(search.toLowerCase()) || c.code.toLowerCase().includes(search.toLowerCase())));
  const add = (code: string) => { onChange([...selected, code]); setSearch(''); };
  const remove = (code: string) => onChange(selected.filter(x => x !== code));
  return (
    <div>
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
        {selected.map(code => (
          <span key={code} style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '3px 6px 3px 10px', borderRadius: 20, fontSize: 12,
            background: '#2a7a4b', color: '#fff', fontWeight: 500,
          }}>
            {labelFor(code)} <span style={{ fontFamily: 'DM Mono, monospace', opacity: 0.7, fontSize: 10 }}>{code}</span>
            <button type="button" onClick={() => remove(code)} aria-label={`${code} entfernen`}
              style={{ marginLeft: 4, background: 'rgba(255,255,255,0.3)', border: 'none', color: '#fff',
                width: 16, height: 16, borderRadius: '50%', cursor: 'pointer', fontSize: 11, lineHeight: '14px' }}>×</button>
          </span>
        ))}
        <button type="button" onClick={() => setOpen(o => !o)}
          style={{ padding: '3px 12px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
            background: 'var(--surface2)', color: 'var(--text2)', border: '1px dashed var(--border2)', fontWeight: 500 }}>
          + Land hinzufügen
        </button>
      </div>
      {open && (
        <div style={{ marginTop: 8, padding: 8, border: '1px solid var(--border2)', borderRadius: 'var(--radius)', background: 'var(--surface)' }}>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} autoFocus
            placeholder="Land suchen…"
            style={{ width: '100%', padding: '6px 10px', fontSize: 12, border: '1px solid var(--border2)',
              borderRadius: 'var(--radius)', marginBottom: 6, background: 'var(--surface2)' }} />
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', maxHeight: 180, overflowY: 'auto' }}>
            {available.map(c => (
              <button key={c.code} type="button" onClick={() => add(c.code)}
                style={{ padding: '3px 9px', borderRadius: 20, fontSize: 11, cursor: 'pointer',
                  background: 'var(--surface2)', color: 'var(--text2)', border: '1px solid var(--border)' }}>
                {c.label} <span style={{ fontFamily: 'DM Mono, monospace', opacity: 0.55, fontSize: 9 }}>{c.code}</span>
              </button>
            ))}
            {available.length === 0 && <span style={{ fontSize: 11, color: 'var(--text3)' }}>Keine Treffer.</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function MultiSelect({ options, selected, onChange, accent }:
  { options: string[]; selected: string[]; onChange: (v: string[]) => void; accent?: 'red' | 'green' }) {
  const toggle = (o: string) => onChange(selected.includes(o) ? selected.filter(x => x !== o) : [...selected, o]);
  const onColor = accent === 'red' ? 'var(--accent)' : '#2a7a4b';
  return (
    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
      {options.map(o => {
        const active = selected.includes(o);
        return (
          <button key={o} type="button" onClick={() => toggle(o)} style={{
            padding: '3px 10px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
            background: active ? onColor : 'var(--surface2)',
            color: active ? '#fff' : 'var(--text2)',
            border: active ? 'none' : '1px solid var(--border)',
            fontWeight: active ? 500 : 400,
          }}>{o}</button>
        );
      })}
    </div>
  );
}

/** Akzeptiert deutsches Format: "1,5", "1.000,5", "1000.50" — interpretiert Punkt
 *  als Dezimaltrennzeichen wenn nur einer auftaucht, sonst als Tausender. */
const parseLocalNum = (v: string): number | null => {
  if (!v) return null;
  const trimmed = v.trim();
  if (!trimmed) return null;
  // Wenn Komma vorhanden: Punkt ist Tausender, Komma ist Dezimal.
  // Sonst: Punkt ist Dezimal.
  let normalized: string;
  if (trimmed.includes(',')) {
    normalized = trimmed.replace(/\./g, '').replace(',', '.');
  } else {
    normalized = trimmed;
  }
  const n = parseFloat(normalized);
  return Number.isFinite(n) ? n : null;
};
const num = parseLocalNum;
const integ = (v: string) => { const n = parseLocalNum(v); return n == null ? null : Math.round(n); };
const mioToStr = (v?: number | null) => v ? String(v / 1_000_000) : '';
const numToStr = (v?: number | null) => v != null ? String(v) : '';

export function ProfileForm({ companyId, existingAssetClassIds = [], onSuccess, onCancel, initial }: ProfileFormProps) {
  const isEdit = !!initial;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auswahlkriterien aus DB
  const [criteria, setCriteria] = useState<Record<string, string[]>>({});
  useEffect(() => {
    apiClient.get<{ data: Record<string, string[]> }>('/asset-classes/criterion-options')
      .then(r => setCriteria(r.data.data ?? {})).catch(() => {});
  }, []);

  // Im Edit-Modus: field_schema der initial-Klasse einmalig laden, sonst bleibt reorderForClass leer
  useEffect(() => {
    if (initial?.asset_class_id) {
      apiClient.get<{ data: unknown; flat: PickerAssetClass[] }>('/asset-classes')
        .then(r => {
          const ac = (r.data.flat ?? []).find(c => c.id === initial.asset_class_id);
          if (ac) setClassSchema(ac.field_schema);
        }).catch(() => {});
    }
  }, [initial?.asset_class_id]);

  // Asset-Klasse (Pflicht, single) + zugehöriges field_schema
  const [assetClassId, setAssetClassId] = useState<string | null>(initial?.asset_class_id ?? null);
  const [assetClassLabel, setAssetClassLabel] = useState<string>(initial?.asset_class_label ?? '');
  const [classSchema, setClassSchema] = useState<PickerAssetClass['field_schema']>(undefined);

  const [profileName, setProfileName] = useState(initial?.profile_name ?? '');

  /**
   * Bei Klassen-Wechsel: Profilname + alle klassenabhängigen Multi-Selects auf
   * die im field_schema.suggested hinterlegten Werte setzen.
   */
  const handleAssetClassChange = (id: string, label: string, ac?: PickerAssetClass) => {
    const sug = ac?.field_schema?.suggested ?? {};
    setAssetClassId(id);
    setAssetClassLabel(label);
    setClassSchema(ac?.field_schema);
    if (!profileName.trim() || profileName.trim() === assetClassLabel.trim()) {
      setProfileName(label);
    }
    setStandortprofil(sug.standortprofil ?? []);
    setRisikoklasse(sug.risikoklasse ?? []);
    setObjektqualitaet(sug.objektqualitaet ?? []);
    setMieter(sug.mieter_betreiber ?? []);
    setEsg(sug.esg_energie ?? []);
    setDealStruktur(sug.deal_struktur ?? []);
  };

  /** Stelle die für die Klasse vorgeschlagenen Werte vorne in die Liste, dann den Rest. */
  type SuggestedKey = keyof NonNullable<NonNullable<PickerAssetClass['field_schema']>['suggested']>;
  const reorderForClass = (pool: string[], category: SuggestedKey): string[] => {
    const sug = classSchema?.suggested?.[category] ?? [];
    if (!sug.length) return pool;
    const inPool = sug.filter(s => pool.includes(s));
    const extras = sug.filter(s => !pool.includes(s));   // Vorschläge die nicht im globalen Pool stehen
    const rest   = pool.filter(p => !sug.includes(p));
    return [...inPool, ...extras, ...rest];
  };

  // Range-Felder (Bereich Anforderungen)
  const [minUnits, setMinUnits] = useState(numToStr(initial?.min_units));
  const [maxUnits, setMaxUnits] = useState(numToStr(initial?.max_units));
  const [minSize, setMinSize]   = useState(numToStr(initial?.min_size_sqm));
  const [maxSize, setMaxSize]   = useState(numToStr(initial?.max_size_sqm));
  const [minYear, setMinYear]   = useState(numToStr(initial?.min_year_built));
  const [maxYear, setMaxYear]   = useState(numToStr(initial?.max_year_built));
  const [volMin, setVolMin]     = useState(mioToStr(initial?.volume_min_eur));
  const [volMax, setVolMax]     = useState(mioToStr(initial?.volume_max_eur));
  const [waltMin, setWaltMin]   = useState(numToStr(initial?.lease_min_years));
  const [waltMax, setWaltMax]   = useState(numToStr(initial?.lease_max_years));

  // Multi-Select aus DB-Optionen
  const [standortprofil, setStandortprofil] = useState<string[]>(initial?.location_quality ?? []);
  const [risikoklasse, setRisikoklasse]     = useState<string[]>(initial?.risk_profile ? [initial.risk_profile] : []);
  const [objektqualitaet, setObjektqualitaet] = useState<string[]>(initial?.object_conditions ?? []);
  const [mieter, setMieter]   = useState<string[]>(initial?.operator_quality ?? []);
  const [esg, setEsg]         = useState<string[]>(initial?.sustainability_labels ?? []);
  const [dealStruktur, setDealStruktur] = useState<string[]>(initial?.transaction_structures ?? []);

  // Location
  const [targetCountries, setTargetCountries] = useState<string[]>(initial?.target_countries ?? []);
  const [targetCities, setTargetCities] = useState((initial?.target_cities ?? []).join(', '));
  const [targetRegions, setTargetRegions] = useState((initial?.target_regions ?? []).join(', '));
  const [locationNotes, setLocationNotes] = useState(initial?.location_description_orig ?? '');

  // Notes
  const [assetNotes, setAssetNotes] = useState(initial?.asset_notes ?? '');
  const [financialNotes, setFinancialNotes] = useState(initial?.financial_notes ?? '');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!assetClassId) { setError('Bitte erst eine Assetklasse wählen.'); return; }
    if (!profileName.trim()) { setError('Profilname ist Pflicht.'); return; }
    setLoading(true); setError(null);
    try {
      const payload = {
        company_id: companyId,
        asset_class_id: assetClassId,
        profile_name: profileName.trim(),
        is_active: true,
        last_source: isEdit ? 'manual' : 'manual',
        // Range-Felder
        min_units: integ(minUnits), max_units: integ(maxUnits),
        min_size_sqm: integ(minSize), max_size_sqm: integ(maxSize),
        min_year_built: integ(minYear), max_year_built: integ(maxYear),
        // Eingabe ist in Mio. € → × 1_000_000 = EUR. Begrenzt auf 999 Mrd.
        // (numeric(15,2) erlaubt knapp 10 Bio., wir nehmen 999 Mrd. als Limit).
        volume_min_eur: (() => { const n = num(volMin); if (n == null) return null; return Math.min(999_999, n) * 1_000_000; })(),
        volume_max_eur: (() => { const n = num(volMax); if (n == null) return null; return Math.min(999_999, n) * 1_000_000; })(),
        volume_strict: true,
        lease_min_years: integ(waltMin), lease_max_years: integ(waltMax),
        // Multi-Select
        location_quality: standortprofil.length ? standortprofil : null,
        risk_profile: risikoklasse[0] ?? null,
        object_conditions: objektqualitaet.length ? objektqualitaet : null,
        operator_quality: mieter.length ? mieter : null,
        sustainability_labels: esg.length ? esg : null,
        transaction_structures: dealStruktur.length ? dealStruktur : null,
        // Location
        target_countries: targetCountries.length ? targetCountries : null,
        target_countries_strict: true,
        target_cities: targetCities.trim() ? targetCities.split(',').map(c => c.trim()).filter(Boolean) : null,
        target_regions: targetRegions.trim() ? targetRegions.split(',').map(r => r.trim()).filter(Boolean) : null,
        location_description_orig: locationNotes.trim() || null,
        // Notes
        asset_notes: assetNotes.trim() || null,
        financial_notes: financialNotes.trim() || null,
      };
      if (isEdit && initial) await apiClient.put(`/profiles/${initial.id}`, payload);
      else                   await apiClient.post('/profiles', payload);
      onSuccess();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      setError(e.response?.data?.error ?? e.message ?? 'Fehler beim Speichern');
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ display: 'grid', gap: 14, maxHeight: '70vh', overflowY: 'auto', paddingRight: 4 }}>

        {/* Assetklasse + Profilname */}
        <FieldGroup label="① Assetklasse (Pflicht — pro Investor max. 1 Profil je Klasse)" tone="accent">
          <AssetClassPicker
            value={assetClassId}
            onChange={(id, label, ac) => handleAssetClassChange(id, label, ac)}
            // andere bereits belegte Klassen sperren — eigene aktuelle aber zulassen
            disabledIds={existingAssetClassIds.filter(id => id !== initial?.asset_class_id)}
            required
          />
          {isEdit && assetClassId && initial?.asset_class_id && assetClassId !== initial.asset_class_id && (
            <div style={{ marginTop: 6, fontSize: 12, color: 'var(--warn)', background: 'var(--warn-bg)', padding: '6px 10px', borderRadius: 4 }}>
              ⚠ Asset-Klasse geändert — bestehende Match-Ergebnisse für dieses Profil werden ungültig.
            </div>
          )}
        </FieldGroup>

        <div>
          <label style={lbl}>Profilname *</label>
          <input style={inp} value={profileName} onChange={e => setProfileName(e.target.value)}
            placeholder='z.B. "Wohnen Value-Add Frankfurt"' required />
        </div>

        {/* Bereich 1 — Anforderungen (Ranges in Boxen) */}
        <div>
          <div style={sectionHead}>② Anforderungen (Bandbreite — von / bis)</div>
          <div style={{ display: 'grid', gap: 10 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <FieldGroup label="Investmentvolumen (Mio. €)" hint="von – bis">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  <input style={numInp} type="number" step="0.5" placeholder="Min" value={volMin} onChange={e => setVolMin(e.target.value)} />
                  <input style={numInp} type="number" step="0.5" placeholder="Max" value={volMax} onChange={e => setVolMax(e.target.value)} />
                </div>
              </FieldGroup>
              <FieldGroup label="Anzahl Einheiten" hint="von – bis">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  <input style={numInp} type="number" placeholder="Min" value={minUnits} onChange={e => setMinUnits(e.target.value)} />
                  <input style={numInp} type="number" placeholder="Max" value={maxUnits} onChange={e => setMaxUnits(e.target.value)} />
                </div>
              </FieldGroup>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <FieldGroup label="Gesamtfläche (m²)" hint="von – bis">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  <input style={numInp} type="number" placeholder="Min" value={minSize} onChange={e => setMinSize(e.target.value)} />
                  <input style={numInp} type="number" placeholder="Max" value={maxSize} onChange={e => setMaxSize(e.target.value)} />
                </div>
              </FieldGroup>
              <FieldGroup label="Baujahr" hint="von – bis">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  <input style={numInp} type="number" placeholder="ab" value={minYear} onChange={e => setMinYear(e.target.value)} />
                  <input style={numInp} type="number" placeholder="bis" value={maxYear} onChange={e => setMaxYear(e.target.value)} />
                </div>
              </FieldGroup>
              <FieldGroup label="WALT (Jahre)" hint="von – bis">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  <input style={numInp} type="number" placeholder="Min" value={waltMin} onChange={e => setWaltMin(e.target.value)} />
                  <input style={numInp} type="number" placeholder="Max" value={waltMax} onChange={e => setWaltMax(e.target.value)} />
                </div>
              </FieldGroup>
            </div>

            <FieldGroup label="Standortprofil" hint={assetClassLabel ? `vorgeschlagen für ${assetClassLabel}` : undefined}>
              <MultiSelect options={reorderForClass(criteria.standortprofil ?? [], 'standortprofil')} selected={standortprofil} onChange={setStandortprofil} accent="green" />
            </FieldGroup>
            <FieldGroup label="Risikoklasse"><MultiSelect options={reorderForClass(criteria.risikoklasse ?? [], 'risikoklasse')} selected={risikoklasse} onChange={setRisikoklasse} accent="green" /></FieldGroup>
            <FieldGroup label="Objektqualität"><MultiSelect options={reorderForClass(criteria.objektqualitaet ?? [], 'objektqualitaet')} selected={objektqualitaet} onChange={setObjektqualitaet} accent="green" /></FieldGroup>
            <FieldGroup label="Mieter / Betreiber"><MultiSelect options={reorderForClass(criteria.mieter_betreiber ?? [], 'mieter_betreiber')} selected={mieter} onChange={setMieter} accent="green" /></FieldGroup>
            <FieldGroup label="ESG / Energie"><MultiSelect options={reorderForClass(criteria.esg_energie ?? [], 'esg_energie')} selected={esg} onChange={setEsg} accent="green" /></FieldGroup>
            <FieldGroup label="Deal-Struktur"><MultiSelect options={reorderForClass(criteria.deal_struktur ?? [], 'deal_struktur')} selected={dealStruktur} onChange={setDealStruktur} accent="green" /></FieldGroup>
          </div>
        </div>

        {/* Bereich 2 — Lage */}
        <div>
          <div style={sectionHead}>③ Lage</div>
          <div style={{ display: 'grid', gap: 10 }}>
            <FieldGroup label="Länder" hint="beliebig viele — Mandate werden nur gegen diese Länder gematcht">
              <CountryMultiSelect selected={targetCountries} onChange={setTargetCountries} />
            </FieldGroup>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <label style={lbl}>Städte (kommagetrennt)</label>
                <input style={inp} value={targetCities} onChange={e => setTargetCities(e.target.value)} placeholder="Frankfurt, Berlin" />
              </div>
              <div>
                <label style={lbl}>Regionen (kommagetrennt)</label>
                <input style={inp} value={targetRegions} onChange={e => setTargetRegions(e.target.value)} placeholder="Rhein-Main, Ruhr" />
              </div>
            </div>
            <div>
              <label style={lbl}>Lage-Notizen</label>
              <input style={inp} value={locationNotes} onChange={e => setLocationNotes(e.target.value)} />
            </div>
          </div>
        </div>

        {/* Bereich 3 — Notes */}
        <div>
          <div style={sectionHead}>④ Notizen</div>
          <div style={{ display: 'grid', gap: 8 }}>
            <textarea style={{ ...inp, minHeight: 50, resize: 'vertical' }} value={assetNotes}
              onChange={e => setAssetNotes(e.target.value)} placeholder="Asset-Notizen…" />
            <textarea style={{ ...inp, minHeight: 50, resize: 'vertical' }} value={financialNotes}
              onChange={e => setFinancialNotes(e.target.value)} placeholder="Finanz-Notizen…" />
          </div>
        </div>

        {/* Ausschluss (markiert als KO) */}
        {error && (
          <div style={{ padding: '10px 14px', background: 'var(--accent-light)', color: 'var(--accent)', borderRadius: 'var(--radius)', fontSize: 13 }}>
            {error}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 16, borderTop: '1px solid var(--border)', marginTop: 4 }}>
        <button type="button" onClick={onCancel} disabled={loading}
          style={{ padding: '8px 16px', background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', fontSize: 13, cursor: 'pointer' }}>
          Abbrechen
        </button>
        <button type="submit" disabled={loading}
          style={{ padding: '8px 20px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius)', fontSize: 13, fontWeight: 500, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}>
          {loading ? 'Speichern…' : isEdit ? 'Änderungen speichern' : 'Profil anlegen'}
        </button>
      </div>
    </form>
  );
}
